import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import {
  Bell,
  BoxArrowRight,
  PersonCircle,
  ArrowLeft,
  CheckCircleFill,
  Clock,
  XCircleFill,
  FileEarmarkText,
} from "react-bootstrap-icons";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import LOGO_URL from "../img/tmis_logo_orig.png";

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_UPDATE = `${API_BASE}/updateDo`;
const ENDPOINT_UPDATE_APPROVED_DO = `${API_BASE}/updateApprovedDo`;
const ENDPOINT_UPLOAD = `${API_BASE}/uploadDocument`;
const ENDPOINT_ACTIVITY_BY_ID = `${API_BASE}/getActivityById`;
const ENDPOINT_LOGGEDIN = `${API_BASE}/getLoggedInDetails`;

const MAX_FILES = 3;
const MAX_FILE_MB = 15;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif";
const FRONTEND_MIN_START = "2026-03-01";

const STATUS_STYLES = {
  Pending: {
    bg: "rgba(245,158,11,.14)",
    text: "#B45309",
    icon: <Clock size={14} />,
  },
  Approved: {
    bg: "rgba(16,185,129,.14)",
    text: "#047857",
    icon: <CheckCircleFill size={14} />,
  },
  Amendment: {
    bg: "rgba(239,68,68,.14)",
    text: "#B91C1C",
    icon: <XCircleFill size={14} />,
  },
  "Re-submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
  },
  "Re-Submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
  },
  Default: {
    bg: "rgba(148,163,184,.16)",
    text: "#475569",
    icon: <Clock size={14} />,
  },
};

function statusPillStyle(s) {
  const { bg, text } = STATUS_STYLES[s] || STATUS_STYLES.Default;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    background: bg,
    color: text,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const toYMD = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{2}-\d{2}-\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split("-");
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const formatSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function ExistingDocsBox({
  title = "Existing documents",
  docs = [],
  formatDate,
}) {
  if (!docs?.length) return null;

  return (
    <div
      className="mt-2"
      style={{
        fontSize: 12,
        background: "#EFF6FF",
        borderRadius: 10,
        padding: "10px 12px",
        border: "1px solid rgba(37,99,235,.18)",
      }}
    >
      <div className="d-flex align-items-center gap-2 mb-1">
        <FileEarmarkText size={14} />
        <div className="fw-bold">{title}</div>
      </div>

      <ul className="mb-1" style={{ paddingLeft: 18 }}>
        {docs.map((d) => (
          <li key={d.doc_id ?? d.id}>
            <span className="text-truncate" title={d.file_name}>
              {d.file_name}
            </span>{" "}
            <span className="text-muted">({formatDate(d.uploaded_date)})</span>
            {String(d.isOldFile ?? d.is_old_file ?? d.is_old ?? "0") ===
              "1" && <span className="ms-2 badge bg-secondary">Old</span>}
          </li>
        ))}
      </ul>

      <div className="text-muted">
        Uploading new files will mark previous files as old for that same
        section.
      </div>
    </div>
  );
}

function FilePickerSection({
  title,
  helper,
  files = [],
  existingDocs = [],
  onPick,
  onRemove,
  selectLabel = "Select Files",
  formatDate,
}) {
  const inputRef = useRef(null);

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-bold">{title}</div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => inputRef.current?.click()}
          style={{ borderRadius: 999 }}
          type="button"
        >
          {selectLabel}
        </Button>
      </div>

      <div className="text-muted" style={{ fontSize: 12 }}>
        {helper}
      </div>

      <div className="text-muted mb-2" style={{ fontSize: 12 }}>
        {files.length} / {MAX_FILES} selected
      </div>

      <ExistingDocsBox
        title={`Existing ${title.toLowerCase()}`}
        docs={existingDocs}
        formatDate={formatDate}
      />

      <input
        ref={inputRef}
        type="file"
        className="d-none"
        multiple
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = "";
        }}
        accept={ACCEPT}
      />

      <div
        className="mt-2"
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "2px dashed #c9ced7",
          padding: 10,
        }}
      >
        {files.length === 0 ? (
          <div
            className="text-muted"
            style={{ minHeight: 42, display: "flex", alignItems: "center" }}
          >
            No new file selected
          </div>
        ) : (
          files.map((f, idx) => (
            <div
              key={`${f.name}-${idx}`}
              className="d-flex align-items-center justify-content-between py-2"
              style={{ borderTop: idx ? "1px dashed #e5e7eb" : "none" }}
            >
              <div
                className="text-truncate"
                title={f.name}
                style={{ maxWidth: "75%" }}
              >
                {f.name}{" "}
                {typeof f.size === "number" && (
                  <small className="text-muted">({formatSize(f.size)})</small>
                )}
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onRemove(idx)}
                style={{
                  borderRadius: 999,
                  padding: "2px 10px",
                  lineHeight: 1.2,
                }}
                type="button"
              >
                Remove
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ProgressModal({ show, pct, msg }) {
  return (
    <Modal show={show} centered backdrop="static" keyboard={false}>
      <Modal.Body className="p-4 custom-font">
        <div className="d-flex align-items-center gap-3 mb-3">
          <Spinner animation="border" size="sm" />
          <div className="fw-bold" style={{ color: "#0f172a" }}>
            Please wait…
          </div>
        </div>

        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          {msg || "Processing…"}
        </div>

        <ProgressBar now={pct} label={`${pct}%`} />
        {pct >= 100 && (
          <div
            className="mt-2"
            style={{ fontSize: 12, color: "#047857", fontWeight: 700 }}
          >
            Completed
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function EditDo() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const location = useLocation();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const editingId = useMemo(() => {
    return (
      routeId ||
      location?.state?.id ||
      location?.state?.doId ||
      query.get("id") ||
      ""
    );
  }, [routeId, location, query]);

  const ownerUserIdFromRoute = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return (
      location?.state?.userId ||
      location?.state?.ownerUserId ||
      qs.get("userId") ||
      ""
    );
  }, [location]);

  const [form, setForm] = useState({
    dateFrom: "",
    dateTo: "",
    remFiles: [],
    tourFiles: [],
    conductedTour: false,
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [existingDocs, setExistingDocs] = useState({ rem: [], con: [] });
  const [editRemarks, setEditRemarks] = useState("");
  const [activityStatus, setActivityStatus] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [progressOpen, setProgressOpen] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);

  const [loadingDetail, setLoadingDetail] = useState(true);
  const [cycle, setCycle] = useState({
    start: FRONTEND_MIN_START,
    end: "",
    daysRemaining: null,
    eligible: true,
    learnReq: null,
    doReq: null,
  });

  const token = useMemo(
    () =>
      localStorage.getItem("authToken") || localStorage.getItem("token") || "",
    []
  );

  const { displayName, username } = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const dn =
        localStorage.getItem("display_name") ||
        user.display_name ||
        localStorage.getItem("username") ||
        "User Acc";
      const un =
        localStorage.getItem("username") ||
        user.username ||
        user.user_name ||
        dn ||
        "User";
      return { displayName: dn, username: un };
    } catch {
      const dn =
        localStorage.getItem("display_name") ||
        localStorage.getItem("username") ||
        "User Acc";
      return { displayName: dn, username: dn };
    }
  }, []);

  const loggedInUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        const id =
          u?.user_id ??
          u?.id ??
          u?.userId ??
          u?.data?.user_id ??
          u?.data?.id ??
          u?.data?.userId ??
          "";
        if (id) return String(id);
      }
      const loose =
        localStorage.getItem("user_id") ||
        localStorage.getItem("userid") ||
        localStorage.getItem("uid") ||
        "";
      return loose ? String(loose) : "";
    } catch {
      return "";
    }
  }, []);

  const targetUserId = useMemo(() => {
    return String(ownerUserIdFromRoute || loggedInUserId || "");
  }, [ownerUserIdFromRoute, loggedInUserId]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/profile", { replace: true });
  };

  const formatDate = (isoOrDMY) => {
    if (!isoOrDMY) return "-";
    if (typeof isoOrDMY === "string" && /^\d{2}-\d{2}-\d{4}$/.test(isoOrDMY)) {
      const [dd, mm, yyyy] = isoOrDMY.split("-");
      const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    const d = new Date(isoOrDMY);
    if (Number.isNaN(d.getTime())) return String(isoOrDMY);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const mergeFiles = (existingArr, pickedList) => {
    const current = Array.isArray(existingArr) ? existingArr : [];
    const picked = Array.from(pickedList || []);

    const tooBig = picked.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      setFormError(`"${tooBig.name}" exceeds ${MAX_FILE_MB} MB limit.`);
      return current;
    }

    const currentKeys = new Set(
      current.map((f) => `${f.name}__${f.size}__${f.lastModified || ""}`)
    );

    const uniquePicked = picked.filter((f) => {
      const key = `${f.name}__${f.size}__${f.lastModified || ""}`;
      if (currentKeys.has(key)) return false;
      currentKeys.add(key);
      return true;
    });

    const merged = [...current, ...uniquePicked].slice(0, MAX_FILES);

    if (current.length + uniquePicked.length > MAX_FILES) {
      setFormError(
        `Maximum ${MAX_FILES} files allowed per section. Extra files were ignored.`
      );
    }

    return merged;
  };

  const onPickRem = (list) => {
    setFormError("");
    setField("remFiles", mergeFiles(form.remFiles, list));
  };

  const onPickTour = (list) => {
    setFormError("");
    setField("tourFiles", mergeFiles(form.tourFiles, list));
  };

  const removeAt = (section, idx) =>
    setField(
      section,
      form[section].filter((_, i) => i !== idx)
    );

  const hasNewFile =
    (form.remFiles?.length || 0) > 0 || (form.tourFiles?.length || 0) > 0;
  const hasExistingFile =
    existingDocs.rem.length > 0 || existingDocs.con.length > 0;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(ENDPOINT_LOGGEDIN, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const obj = data?.data || {};
        let cycleStart = obj?.cycleStart || FRONTEND_MIN_START;
        let cycleEnd = obj?.cycleEnd || "";

        if (cycleStart < FRONTEND_MIN_START) cycleStart = FRONTEND_MIN_START;

        setCycle({
          start: cycleStart,
          end: cycleEnd,
          daysRemaining: obj?.daysRemaining ?? null,
          eligible: obj?.eligible ?? true,
          learnReq: obj?.noOfLearnReq ?? obj?.learnReq ?? null,
          doReq: obj?.noOfDoReq ?? obj?.doReq ?? null,
        });
      } catch {
        setCycle((c) => ({ ...c, start: FRONTEND_MIN_START }));
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!editingId) {
      setLoadingDetail(false);
      setFormError("Missing DO activity id.");
      return;
    }

    (async () => {
      try {
        setLoadingDetail(true);
        setFormError("");

        const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
          params: { module: "do", id: editingId },
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const rows = Array.isArray(data?.data) ? data.data : data?.data || [];
        const r = Array.isArray(rows) ? rows[0] : rows;
        const docs = Array.isArray(data?.docs) ? data.docs : [];

        if (!r) {
          setFormError("Unable to load DO activity details.");
          return;
        }

        const dateFrom = toYMD(r.activity_date_from || r.actDateFr);
        const dateTo = toYMD(r.activity_date_to || r.actDateTo);

        const hasFlag =
          r.hasConductedTour ?? r.has_conducted_tour ?? r.tour_conducted ?? 0;

        const remarks =
          r.remarks || r.remark || r.comments || r.comment || "";

        const grouped = { rem: [], con: [] };
        docs.forEach((d) => {
          if (d.attachment_type === "remNoOfAttach") grouped.rem.push(d);
          if (d.attachment_type === "conNoOfAttach") grouped.con.push(d);
        });

        setForm((prev) => ({
          ...prev,
          dateFrom: dateFrom || "",
          dateTo: dateTo || "",
          conductedTour: Number(hasFlag) === 1,
          remFiles: [],
          tourFiles: [],
        }));

        setExistingDocs(grouped);
        setEditRemarks(remarks || "");
        setActivityStatus(r.status_name || r.status || "");
      } catch (err) {
        setFormError(
          err?.response?.data?.message ||
            "Unable to load DO activity details. Please try again."
        );
      } finally {
        setLoadingDetail(false);
      }
    })();
  }, [editingId, token]);

  const validateForm = () => {
    if (!editingId) return "Missing DO activity id.";
    if (!targetUserId) return "Missing user id. Please login again.";
    if (!token) return "Missing auth token. Please login again.";
    if (!form.dateFrom) return "Please select Activity Date From.";
    if (!form.dateTo) return "Please select Activity Date To.";

    const dFrom = new Date(form.dateFrom);
    const dTo = new Date(form.dateTo);

    if (dTo < dFrom) {
      return "Invalid date range: Activity Date From cannot be earlier than Activity Date To.";
    }

    const currentYear = new Date().getFullYear();
    const fromYear = dFrom.getFullYear();
    if (fromYear > currentYear) {
      return "You can only create DO activities for this year.";
    }

    if (!form.conductedTour) {
      return "You must confirm that you conducted a tour for tourists.";
    }

    if (!hasNewFile && !hasExistingFile) {
      return "Please attach at least one document before submitting.";
    }

    const minStart = cycle.start || FRONTEND_MIN_START;
    const maxEnd = cycle.end;

    if (form.dateFrom < minStart || form.dateTo < minStart) {
      return `Dates must be on/after ${minStart}.`;
    }
    if (maxEnd && (form.dateFrom > maxEnd || form.dateTo > maxEnd)) {
      return `Dates must be on/before ${maxEnd}.`;
    }

    return "";
  };

  const buildDeleteListForType = (attachmentType) => {
    const list =
      attachmentType === "remNoOfAttach"
        ? existingDocs.rem
        : attachmentType === "conNoOfAttach"
        ? existingDocs.con
        : [];

    const ids = (Array.isArray(list) ? list : [])
      .map((d) => d?.doc_id ?? d?.docId ?? d?.id ?? d?.document_id)
      .filter(
        (v) =>
          v !== null && typeof v !== "undefined" && String(v).trim() !== ""
      );

    return Array.from(new Set(ids.map((x) => String(x))));
  };

  async function uploadBucketsSequentially({
    doId,
    remFiles = [],
    tourFiles = [],
  }) {
    const jobs = [];
    if (remFiles.length) jobs.push({ type: "remNoOfAttach", files: remFiles });
    if (tourFiles.length) jobs.push({ type: "conNoOfAttach", files: tourFiles });
    if (!jobs.length) return;

    const grandTotal = jobs.reduce(
      (sum, j) => sum + j.files.reduce((s, f) => s + (f.size || 0), 0),
      0
    );
    let uploadedSoFar = 0;

    setProgressOpen(true);
    setProgressMsg(
      `Uploading ${jobs.reduce((n, j) => n + j.files.length, 0)} file(s)…`
    );
    setProgressPct(0);

    for (let idx = 0; idx < jobs.length; idx++) {
      const { type, files } = jobs[idx];
      const displayIndex = idx + 1;
      const jobsLen = jobs.length;
      const baseUploaded = uploadedSoFar;
      const jobTotal = files.reduce((s, f) => s + (f.size || 0), 0);

      const fd = new FormData();
      fd.append("userId", String(targetUserId));
      fd.append("module", "do");
      fd.append("doId", String(doId));
      fd.append("attachmentType", type);
      fd.append("type", "update");
      fd.append("name", displayName || username || "");
      fd.append("activityName", "");
      fd.append("date", `${form.dateFrom} - ${form.dateTo}`);
      fd.append("typeOfActivity", "");
      fd.append("activityProvider", "");

      const deleteIds = buildDeleteListForType(type);
      if (deleteIds.length) {
        deleteIds.forEach((docId, i) => {
          fd.append(`deleteList[${i}][docId]`, String(docId));
        });
      } else {
        fd.append(`deleteList[0][docId]`, "-1");
      }

      files.forEach((f) => fd.append("files", f));

      const handleProgress = (evt) => {
        const loadedInThisJob = Math.min(evt.loaded || 0, jobTotal);
        const combinedLoaded = baseUploaded + loadedInThisJob;
        const pct = Math.min(
          100,
          Math.round((combinedLoaded * 100) / Math.max(grandTotal, 1))
        );

        setProgressPct(pct);
        setProgressMsg(`Uploading attachments (${displayIndex}/${jobsLen})…`);
      };

      await axios.post(ENDPOINT_UPLOAD, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-userid": String(targetUserId),
          "x-hid": String(doId),
          "x-fldrname": "do",
          "x-filenames": JSON.stringify([]),
        },
        timeout: 180000,
        onUploadProgress: handleProgress,
      });

      uploadedSoFar = baseUploaded + jobTotal;
      setProgressPct(
        Math.min(
          100,
          Math.round((uploadedSoFar * 100) / Math.max(grandTotal, 1))
        )
      );
    }

    setProgressMsg("All attachments uploaded");
    setProgressPct(100);
    await sleep(300);

    setProgressOpen(false);
    setProgressMsg("");
    setProgressPct(0);
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setProgressMsg("");
    setProgressPct(0);
    setProgressOpen(false);

    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }

    const cntRem = form.remFiles?.length || 0;
    const cntCon = form.tourFiles?.length || 0;
    const hasConducted = Number(Boolean(form.conductedTour));

    const isApprovedEdit =
      String(activityStatus || "")
        .trim()
        .toLowerCase() === "approved";

    const payload = {
      doId: String(editingId),
      userId: String(targetUserId),
      dateFrom: form.dateFrom,
      dateTo: form.dateTo,
      hasConductedTour: hasConducted,
      remNoOfAttach: cntRem,
      conNoOfAttach: cntCon,
      statusName: isApprovedEdit ? "Approved" : "Pending",
    };

    const updateUrl = isApprovedEdit
      ? ENDPOINT_UPDATE_APPROVED_DO
      : ENDPOINT_UPDATE;

    setSubmitting(true);
    setProgressOpen(true);
    setProgressPct(10);
    setProgressMsg("Updating DO submission…");

    try {
      const { data } = await axios.post(updateUrl, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-userid": String(targetUserId),
        },
        timeout: 30000,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Update failed");
      }

      setProgressPct(25);
      setProgressMsg("Finalizing update…");

      if ((cntRem || cntCon) > 0) {
        await uploadBucketsSequentially({
          doId: editingId,
          remFiles: form.remFiles,
          tourFiles: form.tourFiles,
        });
      } else {
        setProgressPct(100);
        setProgressMsg("Completed");
        await sleep(250);
        setProgressOpen(false);
        setProgressMsg("");
        setProgressPct(0);
      }

      setShowSuccess(true);
    } catch (err2) {
      setProgressOpen(false);
      setProgressMsg("");
      setProgressPct(0);
      setFormError(
        err2?.response?.data?.message ||
          err2?.message ||
          "Failed to update. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="learn-wrap custom-font"
      style={{ background: PANEL_BG, minHeight: "100vh" }}
    >
      <ProgressModal show={progressOpen} pct={progressPct} msg={progressMsg} />

      <header
        className="learn-topbar"
        style={{ height: 80, background: "#fff" }}
      >
        <div className="container-xxl h-100 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <img
              src={LOGO_URL}
              alt="TMIS"
              style={{ height: 50, objectFit: "contain" }}
            />
          </div>
          <div
            className="d-flex align-items-center gap-4"
            style={{ color: "#333" }}
          >
            <Bell size={20} color="#1A3584" />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
            />
            <span>{displayName}</span>
            <PersonCircle
              size={23}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/profile")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/profile");
              }}
            />
          </div>
        </div>
      </header>

      <div
        style={{
          background: NAV_BLUE,
          color: "#fff",
          padding: "12px 0",
          boxShadow: "0 2px 0 rgba(0,0,0,0.04)",
        }}
      >
        <div
          className="container-xxl d-flex align-items-center"
          style={{ gap: 12 }}
        >
          <span
            role="button"
            aria-label="Go back"
            tabIndex={0}
            onClick={handleBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} color="#ffffff" />
          </span>
          <h4 className="m-0 fw-bold">Edit DO activity</h4>
        </div>
      </div>

      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} lg={8} xl={7}>
            <Card
              className="shadow-sm"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: PANEL_SURFACE,
              }}
            >
              <Card.Body className="p-4 p-md-5">
                {activityStatus ? (
                  <div className="mb-3">
                    <span style={statusPillStyle(activityStatus)}>
                      {(STATUS_STYLES[activityStatus] || STATUS_STYLES.Default)
                        .icon || null}
                      {activityStatus}
                    </span>
                  </div>
                ) : null}

                <Alert
                  variant="primary"
                  className="mb-3 d-flex justify-content-between align-items-start"
                >
                  <div>
                    <div className="fw-bold mb-1">
                      Editing existing DO activity
                    </div>
                    <div style={{ fontSize: 13 }}>
                      Update DO submission and upload new documents if
                      needed.
                    </div>
                    {loadingDetail && (
                      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                        <Spinner animation="border" size="sm" /> Loading
                        details…
                      </div>
                    )}
                  </div>
                </Alert>

                {editRemarks && (
                  <Alert variant="warning" className="mb-3">
                    <div className="fw-bold mb-1">Remarks from reviewer</div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{editRemarks}</div>
                  </Alert>
                )}

                {formError && (
                  <Alert variant="danger" className="mb-3">
                    {formError}
                  </Alert>
                )}

                <Form onSubmit={onSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="dateFrom">
                        <Form.Label className="fw-bold mb-1">
                          Activity Date From
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateFrom}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField("dateFrom", v);
                            if (form.dateTo && v && form.dateTo < v) {
                              setField("dateTo", "");
                            }
                          }}
                          min={cycle.start || FRONTEND_MIN_START}
                          max={cycle.end || undefined}
                          required
                          disabled={loadingDetail}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="dateTo">
                        <Form.Label className="fw-bold mb-1">
                          Activity Date To
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateTo}
                          onChange={(e) => setField("dateTo", e.target.value)}
                          min={
                            form.dateFrom || cycle.start || FRONTEND_MIN_START
                          }
                          max={cycle.end || undefined}
                          required
                          disabled={loadingDetail}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <FilePickerSection
                    title="Supporting Document (Proof of Remuneration)"
                    helper={`(Maximum ${MAX_FILES} files allowed, ≤ ${MAX_FILE_MB} MB each)`}
                    files={form.remFiles}
                    existingDocs={existingDocs.rem}
                    onPick={onPickRem}
                    onRemove={(idx) => removeAt("remFiles", idx)}
                    selectLabel="Select Files"
                    formatDate={formatDate}
                  />

                  <FilePickerSection
                    title="Supporting Document (Proof of Tour Conducted)"
                    helper={`(Maximum ${MAX_FILES} files allowed, ≤ ${MAX_FILE_MB} MB each)`}
                    files={form.tourFiles}
                    existingDocs={existingDocs.con}
                    onPick={onPickTour}
                    onRemove={(idx) => removeAt("tourFiles", idx)}
                    selectLabel="Select Files"
                    formatDate={formatDate}
                  />

                  <Form.Group className="mb-3" controlId="conductedTour">
                    <Form.Check
                      type="checkbox"
                      label="I conducted a tour for tourists and have received remuneration"
                      checked={form.conductedTour}
                      onChange={(e) =>
                        setField("conductedTour", e.target.checked)
                      }
                      className="small"
                      disabled={loadingDetail}
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={handleBack}
                      style={{ minWidth: 120, borderRadius: 20 }}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      disabled={submitting || loadingDetail}
                      style={{
                        background: BRAND,
                        border: "none",
                        minWidth: 140,
                        borderRadius: 20,
                      }}
                    >
                      {submitting ? "Updating..." : "Update"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        backdrop="static"
        keyboard
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "rgba(16,185,129,0.12)",
            }}
          >
            <CheckCircleFill size={44} color="#10B981" />
          </div>
          <h5 className="fw-bold mb-2">Update Successful</h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            Your DO activity was updated successfully.
          </div>
          <div className="d-flex justify-content-center gap-2">
            <Button
              onClick={() => {
                setShowSuccess(false);

                if (window.history.length > 1) {
                  navigate(-1);
                }
              }}
              style={{ background: BRAND, border: "none", minWidth: 120 }}
            >
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}